import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "./backendconfig";
import { usePublicList } from "./usePublicList";
import "./Gallery.css";

const backend = BACKEND_URL.replace(/\/+$/, "");
const PAGE_SIZE = 12;

function imageUrl(src) {
  return src ? new URL(src, `${backend}/`).href : "";
}

export default function Gallery() {
  const { items, loading, error } = usePublicList(
    `${backend}/media.php?type=gallery`,
  );

  const [page, setPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const headingRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleItems = items.slice(startIndex, startIndex + PAGE_SIZE);

  const selectedItem = selectedIndex !== null ? items[selectedIndex] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setPage((previous) => Math.min(previous, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= items.length) {
      setSelectedIndex(null);
    }
  }, [items.length, selectedIndex]);

  const changePage = (nextPage) => {
    setPage(Math.max(1, Math.min(nextPage, totalPages)));

    headingRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const showPrevious = () => {
    setSelectedIndex((index) =>
      index === null ? null : (index - 1 + items.length) % items.length,
    );
  };

  const showNext = () => {
    setSelectedIndex((index) =>
      index === null ? null : (index + 1) % items.length,
    );
  };

  // Compact pagination for large galleries.
  const pageNumbers = Array.from(
    new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages]),
  )
    .filter((number) => number >= 1 && number <= totalPages)
    .sort((a, b) => a - b);

  return (
    <main className="ltc-gallery-page">
      <div className="ltc-gallery-container">
        <header ref={headingRef} className="ltc-gallery-heading">
          <span>OUR GALLERY</span>
          <h1>Learning, Creativity & Inspiration</h1>
          <p>Explore moments from our embroidery design classes.</p>
          <Link to="/">← Back to home</Link>
        </header>

        {loading ? (
          <p className="ltc-gallery-message" role="status">
            Loading gallery…
          </p>
        ) : error ? (
          <p className="ltc-gallery-message ltc-gallery-error" role="alert">
            {error}
          </p>
        ) : items.length === 0 ? (
          <p className="ltc-gallery-message">
            No gallery images available yet.
          </p>
        ) : (
          <>
            <div className="ltc-gallery-grid">
              {visibleItems.map((item, index) => (
                <button
                  key={item.id || item.src}
                  type="button"
                  className="ltc-gallery-card"
                  onClick={() => setSelectedIndex(startIndex + index)}
                  aria-label={`Enlarge ${item.title || "gallery image"}`}
                >
                  <img
                    src={imageUrl(item.src)}
                    alt={item.alt || item.title || "Embroidery design class"}
                    loading="eager"
                    width="800"
                    height="600"
                  />

                  <span className="ltc-gallery-overlay" aria-hidden="true">
                    <span>View Image ↗</span>
                  </span>
                </button>
              ))}
            </div>

            {totalPages > 1 && (
              <nav
                className="ltc-gallery-pagination"
                aria-label="Gallery pages"
              >
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => changePage(currentPage - 1)}
                >
                  ← Previous
                </button>

                {pageNumbers.map((number, index) => (
                  <React.Fragment key={number}>
                    {index > 0 && number - pageNumbers[index - 1] > 1 && (
                      <span
                        className="ltc-gallery-pagination-gap"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    )}

                    <button
                      type="button"
                      className={currentPage === number ? "is-active" : ""}
                      aria-label={`Page ${number}`}
                      aria-current={currentPage === number ? "page" : undefined}
                      onClick={() => changePage(number)}
                    >
                      {number}
                    </button>
                  </React.Fragment>
                ))}

                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => changePage(currentPage + 1)}
                >
                  Next →
                </button>
              </nav>
            )}
          </>
        )}
      </div>

      {selectedItem && (
        <GalleryLightbox
          item={selectedItem}
          index={selectedIndex}
          total={items.length}
          onClose={() => setSelectedIndex(null)}
          onPrevious={showPrevious}
          onNext={showNext}
        />
      )}
    </main>
  );
}

function GalleryLightbox({ item, index, total, onClose, onPrevious, onNext }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const dialog = dialogRef.current;

    document.body.style.overflow = "hidden";

    if (dialog && !dialog.open) {
      dialog.showModal();
      closeRef.current?.focus();
    }

    return () => {
      dialog?.close();
      document.body.style.overflow = previousOverflow;

      if (previousFocus instanceof HTMLElement) {
        previousFocus.focus();
      }
    };
  }, []);

  useEffect(() => {
    setImageError(false);
  }, [item.src]);

  return (
    <dialog
      ref={dialogRef}
      className="ltc-gallery-lightbox"
      aria-label="Gallery image preview"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onKeyDown={(event) => {
        if (total <= 1) return;

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          onPrevious();
        }

        if (event.key === "ArrowRight") {
          event.preventDefault();
          onNext();
        }
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="ltc-gallery-popup">
        <div className="ltc-gallery-popup-header">
          <span role="status">
            Image {index + 1} of {total}
          </span>

          <button
            ref={closeRef}
            type="button"
            className="ltc-gallery-close"
            onClick={onClose}
            aria-label="Close image preview"
          >
            ×
          </button>
        </div>

        <div className="ltc-gallery-popup-viewer">
          <button
            type="button"
            className="ltc-gallery-image-arrow"
            onClick={onPrevious}
            disabled={total <= 1}
            aria-label="Previous image"
          >
            ❮
          </button>

          <div className="ltc-gallery-popup-image">
            {imageError ? (
              <p role="status">Unable to load this image.</p>
            ) : (
              <img
                key={item.src}
                src={imageUrl(item.src)}
                alt={item.alt || item.title || "Gallery image"}
                onError={() => setImageError(true)}
              />
            )}
          </div>

          <button
            type="button"
            className="ltc-gallery-image-arrow"
            onClick={onNext}
            disabled={total <= 1}
            aria-label="Next image"
          >
            ❯
          </button>
        </div>
      </div>
    </dialog>
  );
}
